import { appendBrandIds } from '@/lib/brand-filter-params';
import type { SellChannel } from '@/types/sell-channel';
import type { PrintUnavailableReason } from '@/types/brand';
import type { BankTransfer } from './bank-transfers-client';
import type { DiscountPosChannel, DiscountRewardItem, DiscountType } from './discounts-client';
import type { DraftBlocker, PaymentPreference } from './draft-order-client';
import type { LoyaltyRedemption } from './loyalty-redemption';
import type { OrderDeliverySummary } from './deliveries-client';
import { ApiError, toApiError } from './api-error';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body } = options;
    const url = `${API_BASE_URL}${endpoint}`;

    const fetchOptions: RequestInit = {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    };

    if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
        throw new Error(`Unauthorized (${response.status})`);
    }

    if (!response.ok) {
        // Carries the body's `code`, so callers can branch on refusals like PAYMENT_IN_PROGRESS.
        throw await toApiError(response);
    }

    return response.json();
}

export type OrderStatus =
    | 'draft'
    | 'pending'
    | 'confirmed'
    | 'completed'
    | 'pending_payment'
    | 'pos_push_failed'
    | 'pushed_to_external_pending'
    | 'pushed_to_external'
    | 'cancelled'
    | 'abandoned'
    | 'superseded';

export interface OrderItemComment {
    text: string;
    quantity: number;
}

export interface OrderItemOption {
    /** Absent on lines recorded before the ids were persisted; the cart editor drops those. */
    groupId?: string;
    optionId?: string;
    groupName: string;
    optionName: string;
    priceDelta: number;
}

export interface OrderItem {
    catalogItemId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    options?: OrderItemOption[];
    comments?: OrderItemComment[] | string;
    subtotal: number;
}

export interface OrderCustomer {
    id: string;
    phone: string;
    name?: string;
}

export interface OrderPaymentAttempt {
    at: string;
    method: 'saved_card' | 'payment_link';
    /** `released`: the order went back to a draft and its link was closed (switch of method, edit). */
    event: 'approved' | 'declined' | 'failed' | 'link_generated' | 'paid' | 'refunded' | 'released';
    /** Entries sharing a paymentId (or linkId) are lifecycle events of the same attempt. */
    paymentId?: string | null;
    linkId?: string | null;
    detail?: string | null;
}

/**
 * The coupon as it was when the order confirmed, frozen onto the order itself.
 *
 * Snapshotted rather than referenced on purpose: a merchant editing or disabling the coupon later
 * must not change what an already-confirmed order was charged, so this is what the UI must render —
 * never the current coupon definition.
 */
export interface AppliedCoupon {
    discountId: string;
    /** Null on an automatic promotion — nobody typed it, so it is named by `name` instead. */
    code?: string | null;
    name?: string | null;
    type: DiscountType;
    value: number;
    rewardItems?: DiscountRewardItem[] | null;
    excludePromoItems: boolean;
    minimumOrderAmount?: number | null;
    /** Money actually taken off by THIS snapshot — items AND fee money together. */
    discountAmount: number;
    /** C3 — the part of {@link discountAmount} this snapshot took off the delivery fee. Absent = none. */
    deliveryFeeDiscountAmount?: number;
    /** C3 — a whole-cart discount that also discounted the fee. Absent = false. */
    appliesToDeliveryFee?: boolean;
    /** §C2 — whether it was allowed to ride along with another. False on a legacy snapshot. */
    stackable: boolean;
    /** §C2 — which of the POS's lists it came from; absent on a merchant-authored snapshot. */
    posChannel?: DiscountPosChannel | null;
}

/** The two thermal tickets a printer can be set up for: the kitchen's comanda and the customer's pedido. */
export type PrintTicketType = 'comanda' | 'pedido';

/** The latest outcome munchies-api reported for one ticket type. */
export interface OrderPrintTicketStatus {
    status: 'printed' | 'failed' | 'expired';
    printerName: string;
    error: string | null;
    /** ISO — when the outcome was observed. */
    at: string;
    jobId: string;
}

/**
 * `order.printing` — thermal printing as the order sees it: the last print request (on acceptance or
 * a reprint) and the latest outcome per ticket type. Read it through `printStatusOf`.
 */
export interface OrderPrintingSummary {
    /** ISO — when the print request munchies-api last answered was first queued (all retries share it). */
    requestedAt?: string;
    /** Jobs that request queued. 0 = the branch has no printer: only the browser print is offered. */
    jobsQueued?: number;
    /** The ticket types those jobs print — "Impreso" waits for every one of them. */
    ticketTypes?: PrintTicketType[];
    /** ISO — a print was queued and munchies-api has not answered it yet when newer than the rest. */
    queuedAt?: string;
    /** The last request never reached a printer queue (`status`: munchies-api's 4xx, or null). */
    requestError?: { status: number | null; at: string } | null;
    comanda?: OrderPrintTicketStatus;
    pedido?: OrderPrintTicketStatus;
    /**
     * ISO — the acceptance print was deliberately not asked for: the branch cannot print
     * (`skipReason`). Nothing was queued and nothing will come back: no chip, no reprint.
     */
    skippedAt?: string;
    skipReason?: PrintUnavailableReason;
}

export interface Order {
    id: string;
    brandId: string;
    /** Set by aggregate-aware responses so a row can be labeled without a second lookup. */
    brandName?: string;
    locationName?: string | null;
    customerId: string;
    customer?: OrderCustomer;
    /**
     * The name THIS order goes under, when one was given for it — the web checkout's name field, or
     * the name the chat agent asked for. An override of the customer's own name, so it is never a
     * rename: read it through {@link orderCustomerName}, never on its own.
     */
    customerName?: string;
    conversationId?: string;
    /**
     * The chat the customer came in from, when the order was PLACED somewhere else — set only where
     * the two differ, i.e. a cart the agent opened in chat and the customer finished on the web.
     * Optional for the same reason `channel` is: a payload that predates it must read as "no origin
     * known" rather than as "no chat involved".
     */
    originConversationId?: string;
    /**
     * The surface the order was AUTHORED on (D11 / D16). Required on the backend's domain object and
     * spread verbatim onto every order response, but kept optional here so a surface that has not
     * re-read the order (or an older cached payload) degrades to "unknown" rather than lying — see
     * `statusMessageOffer`, which treats unknown as "not WhatsApp".
     */
    channel?: SellChannel;
    status: OrderStatus;
    items: OrderItem[];
    deliveryType?: 'delivery' | 'pickup';
    deliveryAddress?: Record<string, unknown>;
    deliveryFee?: number;
    estimatedDeliveryMinutes?: number;
    locationId?: string;
    paymentPreference?: 'cash' | 'send_pos_for_card' | 'bank_transfer' | 'mercado_pago' | 'card' | 'pay_at_store';
    /** The order's ACTIVE transfer proof (latest non-superseded `bank_transfer` row). */
    bankTransfer?: BankTransfer;
    /** Tip captured in the checkout flow. Already folded into `total`. */
    tipAmount?: number;
    /**
     * Discount discount already subtracted from `total`. 0 when no coupon is applied — and also when a
     * coupon IS applied but currently takes nothing off (its reward item is not in the cart), which
     * is why `appliedDiscounts` must be checked separately rather than inferred from this being > 0.
     */
    discountAmount?: number;
    /**
     * C4 — the part of {@link discountAmount} that came off the delivery fee; 0/absent when none.
     * The net fee is `deliveryFee − deliveryFeeDiscountAmount`.
     */
    deliveryFeeDiscountAmount?: number;
    /** Every discount frozen onto the order, in application order: typed coupons, then automatics. */
    appliedDiscounts?: AppliedCoupon[];
    /**
     * @deprecated §C2 replaced this with {@link Order.appliedDiscounts}. Read through
     * `appliedDiscountList` — it is the only thing that still knows this field exists.
     */
    appliedDiscount?: AppliedCoupon;
    /**
     * Loyalty money spent on this order (cashback balance, loyalty reward coupon, …). Its applied
     * amount is INCLUDED in {@link discountAmount} and never listed in {@link appliedDiscounts}.
     * Read through `loyaltyRedemptionOf` (`lib/loyalty-redemption`). Null/absent = none.
     */
    loyaltyDiscount?: LoyaltyRedemption | null;
    total: number;
    currency: string;
    notes?: string;
    cancelledReason?: string;
    confirmedAt?: string;
    /** When the store accepted it (pending → confirmed), by hand or by the branch's auto-accept. */
    acceptedAt?: string | null;
    /** Thermal printing — see {@link OrderPrintingSummary}. Null/absent = never sent to a printer. */
    printing?: OrderPrintingSummary | null;
    /**
     * The rider booking, as the dispatch module last wrote it (read-only, like `printing`). Null/absent
     * = no delivery provider was ever asked. The full dispatch is `GET /orders/:id/delivery`.
     */
    delivery?: OrderDeliverySummary | null;
    posError?: string;
    /** True when posError came from a POS 4xx — deterministic, retrying the push cannot succeed. */
    posErrorPermanent?: boolean;
    isSandbox?: boolean;
    trackingUrl?: string;
    paymentLinkUrl?: string;
    /** Menteo payment link collecting this order. */
    menteoPaymentLinkId?: string;
    /** Paid Menteo payment id — deep-links to the dashboard payment detail. */
    menteoPaymentId?: string;
    /** Loyalty member linked on the paid checkout — deep-links to the dashboard profile. */
    loyaltyCustomerId?: string;
    paymentDeadlineAt?: string;
    paymentConfirmedAt?: string;
    /**
     * What "Cambiar forma de pago" may move this order to: the methods the brand offers for its
     * fulfilment type, minus online card and minus the current one, in the server's order. Sent by
     * `GET /orders/:id` only, and only while the order is unpaid (`canSwitchPaymentMethod`); absent
     * means there is nothing to offer.
     */
    switchablePaymentMethods?: PaymentPreference[];
    paymentAttempts?: OrderPaymentAttempt[];
    externalPosOrderId?: string;
    externalPosProvider?: string;
    createdAt: string;
    updatedAt: string;
}

interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export const VALID_NEXT_STATUSES: Record<OrderStatus, OrderStatus[]> = {
    draft: [
        'pending',
        'pending_payment',
        'pushed_to_external_pending',
        'pushed_to_external',
        'cancelled',
        'abandoned',
        // The customer identified themselves on the web with a cart of their own and it won the
        // claim (D17). Draft-only: a placed order is never superseded.
        'superseded',
    ],
    pending: ['confirmed', 'cancelled'],
    confirmed: ['completed', 'cancelled'],
    completed: [],
    // Payment (not the operator) advances a pending_payment order — only cancel is manual.
    pending_payment: ['cancelled'],
    // Paid but the POS push failed — the operator resolves it: mark as keyed into the POS by hand
    // (→ pushed_to_external) or cancel (+ refund from the dashboard). `pending` mirrors the backend's
    // table but no surface offers it: a POS brand never accepts an order in Menteo.
    pos_push_failed: ['pending', 'pushed_to_external', 'cancelled'],
    pushed_to_external_pending: ['pushed_to_external', 'cancelled'],
    pushed_to_external: [],
    cancelled: [],
    abandoned: [],
    superseded: [],
};

/**
 * Retired carts: they were never placed, so they are not orders an operator works. Kept out of the
 * list's default view and out of its count — the board leaves them out by giving them no lane.
 */
export const RETIRED_DRAFT_STATUSES: OrderStatus[] = ['abandoned', 'superseded'];

export function isRetiredDraft(status: OrderStatus): boolean {
    return RETIRED_DRAFT_STATUSES.includes(status);
}

export const STATUS_LABELS: Record<OrderStatus, string> = {
    draft: 'Borrador',
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    completed: 'Completado',
    pending_payment: 'Esperando pago',
    pos_push_failed: 'Pagado — error POS',
    pushed_to_external_pending: 'Pendiente de pago',
    pushed_to_external: 'Enviado a POS',
    cancelled: 'Cancelado',
    abandoned: 'Abandonado',
    superseded: 'Reemplazado por el carrito web',
};

export const STATUS_COLORS: Record<OrderStatus, string> = {
    draft: 'bg-muted text-muted-foreground',
    pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    confirmed: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    completed: 'bg-emerald-200 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-200',
    pending_payment: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
    pos_push_failed: 'bg-red-600 text-white',
    pushed_to_external_pending: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
    pushed_to_external: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    abandoned: 'bg-muted text-muted-foreground',
    superseded: 'bg-muted text-muted-foreground',
};

/**
 * The name this order goes to the store under: the one given for THIS order, falling back to the
 * customer's own (their WhatsApp profile name, in practice). Mirrors the backend's
 * `orderCustomerName`, which is what the board projection already resolves for its cards — every
 * surface that prints a name has to agree with them, or the same order reads "Sin nombre" in the
 * panel and "Teofilo" on the card it was opened from.
 */
export function orderCustomerName(order: Pick<Order, 'customerName' | 'customer'>): string | undefined {
    return order.customerName?.trim() || order.customer?.name?.trim() || undefined;
}

export function getForwardStatus(order: Pick<Order, 'status'>): OrderStatus | null {
    // pos_push_failed has SEVERAL manual resolutions — the detail panel's action card owns them.
    if (order.status === 'pos_push_failed') return null;
    const next = VALID_NEXT_STATUSES[order.status].filter((s) => s !== 'cancelled');
    if (next.length === 0) return null;
    return next[0];
}

export function getForwardActionLabel(order: Order): string | null {
    switch (order.status) {
        case 'pending':
            return 'Confirmar';
        case 'confirmed':
            return 'Completar';
        default:
            return null;
    }
}

export function canCancel(status: OrderStatus): boolean {
    return VALID_NEXT_STATUSES[status].includes('cancelled');
}

/**
 * An unpaid order can be moved to another payment method — "le pago en efectivo". Not a status move
 * (`pending_payment` still only goes to `cancelled` by hand): the server releases the order to a draft
 * and confirms it again with the new method, refusing while a charge may be in flight.
 */
export function canSwitchPaymentMethod(order: Pick<Order, 'status' | 'paymentConfirmedAt'>): boolean {
    return order.status === 'pending_payment' && !order.paymentConfirmedAt;
}

/**
 * Why the server refused to release, switch or cancel an unpaid order: a charge may still land
 * (`PAYMENT_IN_PROGRESS`, `CHARGE_IN_VERIFICATION`), it already did (`PAYMENT_ALREADY_SETTLED` — the
 * order was placed underneath the operator), or the order is not unpaid any more (`NOT_RELEASABLE`).
 * Each has its own `orders:errors.*` sentence.
 */
export type OrderPaymentHoldCode =
    | 'PAYMENT_IN_PROGRESS'
    | 'CHARGE_IN_VERIFICATION'
    | 'PAYMENT_ALREADY_SETTLED'
    | 'NOT_RELEASABLE';

const PAYMENT_HOLD_CODES: readonly string[] = [
    'PAYMENT_IN_PROGRESS',
    'CHARGE_IN_VERIFICATION',
    'PAYMENT_ALREADY_SETTLED',
    'NOT_RELEASABLE',
] satisfies OrderPaymentHoldCode[];

/** The hold code behind a failed order request, or null when the failure was something else. */
export function paymentHoldCode(error: unknown): OrderPaymentHoldCode | null {
    if (!(error instanceof ApiError) || !PAYMENT_HOLD_CODES.includes(error.code)) return null;
    return error.code as OrderPaymentHoldCode;
}

export interface SwitchPaymentMethodPayload {
    paymentPreference: PaymentPreference;
    useSavedCard?: boolean;
}

/**
 * `confirmed`: the order is placed again with the new method (a card switch comes back with a fresh
 * link). `blocked`: the re-confirmation was refused and the order is a normal draft again — `blockers`
 * says why, and the cart is where the operator fixes it.
 */
export type SwitchPaymentMethodResult =
    | { outcome: 'confirmed'; order: Order; paymentLinkUrl?: string; paymentDeadlineAt?: string }
    | { outcome: 'blocked'; order: Order; blockers: DraftBlocker[] };

export interface ItemSales {
    catalogItemId: string;
    /** Name the product was sold under (from the order lines) — survives catalog re-syncs. */
    name: string;
    locationId: string | null;
    totalQuantity: number;
    orderCount: number;
}

export const ordersClient = {
    async list(params?: { brandId?: string; status?: OrderStatus; brandIds?: string[] }): Promise<ApiResponse<Order[]>> {
        const searchParams = new URLSearchParams();
        if (params?.brandId) searchParams.set('brandId', params.brandId);
        if (params?.status) searchParams.set('status', params.status);
        appendBrandIds(searchParams, params?.brandIds);
        const query = searchParams.toString();
        return api<ApiResponse<Order[]>>(`/orders${query ? `?${query}` : ''}`);
    },

    async getToday(params: {
        locationId?: string;
        brandId?: string;
        from?: string;
        to?: string;
        search?: string;
        /** Aggregate sessions only: narrow to these brands (omitted = every brand in scope). */
        brandIds?: string[];
    }): Promise<ApiResponse<Order[]>> {
        const searchParams = new URLSearchParams();
        if (params.locationId) searchParams.set('locationId', params.locationId);
        if (params.brandId) searchParams.set('brandId', params.brandId);
        if (params.from) searchParams.set('from', params.from);
        if (params.to) searchParams.set('to', params.to);
        if (params.search) searchParams.set('search', params.search);
        appendBrandIds(searchParams, params.brandIds);
        const query = searchParams.toString();
        return api<ApiResponse<Order[]>>(`/orders/today${query ? `?${query}` : ''}`);
    },

    async getById(id: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}`);
    },

    /** Units sold per catalog item and location across the brand's placed orders. */
    async getItemSales(params?: { days?: number; locationId?: string }): Promise<ApiResponse<ItemSales[]>> {
        const searchParams = new URLSearchParams();
        if (params?.days) searchParams.set('days', String(params.days));
        if (params?.locationId) searchParams.set('locationId', params.locationId);
        const query = searchParams.toString();
        return api<ApiResponse<ItemSales[]>>(`/orders/item-sales${query ? `?${query}` : ''}`);
    },

    async getByConversation(conversationId: string): Promise<ApiResponse<Order[]>> {
        return api<ApiResponse<Order[]>>(`/orders/conversation/${conversationId}`);
    },

    /** Short-lived presigned URL for the transfer receipt stored in S3 (orders paid by transfer). */
    async getTransferReceiptUrl(id: string): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
        return api<ApiResponse<{ url: string; expiresIn: number }>>(`/orders/${id}/transfer-receipt-url`);
    },

    async updateStatus(id: string, status: OrderStatus, cancelledReason?: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}/status`, {
            method: 'PATCH',
            body: { status, cancelledReason },
        });
    },

    /**
     * Put an unpaid order on another payment method. Refusals answer 409 with a `code`
     * (`PAYMENT_IN_PROGRESS`, `CHARGE_IN_VERIFICATION`, `PAYMENT_ALREADY_SETTLED`, `NOT_RELEASABLE`) and
     * 422 `PAYMENT_METHOD_NOT_ACCEPTED` when the brand does not offer the method for this fulfilment.
     * The customer is not messaged about it.
     */
    async switchPaymentMethod(
        id: string,
        payload: SwitchPaymentMethodPayload,
    ): Promise<ApiResponse<SwitchPaymentMethodResult>> {
        return api<ApiResponse<SwitchPaymentMethodResult>>(`/orders/${id}/payment/switch`, {
            method: 'POST',
            body: payload,
        });
    },

    /**
     * Retry the POS push of a paid order parked in pos_push_failed. Always answers 200 with the
     * reloaded order — read its status to know whether the push healed or it stays parked.
     */
    async retryPosPush(id: string): Promise<ApiResponse<Order>> {
        return api<ApiResponse<Order>>(`/orders/${id}/retry-pos-push`, { method: 'POST' });
    },

    /**
     * Reprint an accepted order on the branch's thermal printers. Answers as soon as the reprint is
     * queued; the outcome arrives later on `order.printing` (and the board feed). 409
     * `ORDER_NOT_ACCEPTED` for an order the store has not accepted yet.
     */
    async requestPrint(id: string): Promise<{ queued: true }> {
        return api<{ queued: true }>(`/orders/${id}/print`, { method: 'POST' });
    },
};
